import { useEffect, useRef, useState } from 'react';

interface NavigationHighlightProps {
  targetNavHref: string;
}

interface OverlayStyles {
  top: React.CSSProperties;
  right: React.CSSProperties;
  bottom: React.CSSProperties;
  left: React.CSSProperties;
  highlight: React.CSSProperties;
}

export function NavigationHighlight({ targetNavHref }: NavigationHighlightProps) {
  const [overlayStyles, setOverlayStyles] = useState<OverlayStyles | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const targetRef = useRef<HTMLElement | null>(null);
  const prevStylesRef = useRef<{ position?: string; zIndex?: string } | null>(null);

  const updatePositions = () => {
    const navElement = document.querySelector(`[data-nav-item="${targetNavHref}"]`) as HTMLElement | null;
    targetRef.current = navElement;

    if (navElement) {
      const rect = navElement.getBoundingClientRect();
      const padding = 8;

      // Elevate the target element above overlays and dialog overlay, but below the dialog content
      const computed = window.getComputedStyle(navElement);
      if (!prevStylesRef.current) {
        prevStylesRef.current = {
          position: navElement.style.position,
          zIndex: navElement.style.zIndex,
        };
      }
      if (computed.position === 'static') {
        navElement.style.position = 'relative';
      }
      navElement.style.zIndex = '9998';

      setOverlayStyles({
        top: {
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: `${rect.top - padding}px`,
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          pointerEvents: 'none',
          zIndex: 9997,
          transition: 'all 0.3s ease-in-out',
        },
        right: {
          position: 'fixed',
          top: `${rect.top - padding}px`,
          left: `${rect.right + padding}px`,
          right: 0,
          height: `${rect.height + padding * 2}px`,
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          pointerEvents: 'none',
          zIndex: 9997,
          transition: 'all 0.3s ease-in-out',
        },
        bottom: {
          position: 'fixed',
          top: `${rect.bottom + padding}px`,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          pointerEvents: 'none',
          zIndex: 9997,
          transition: 'all 0.3s ease-in-out',
        },
        left: {
          position: 'fixed',
          top: `${rect.top - padding}px`,
          left: 0,
          width: `${rect.left - padding}px`,
          height: `${rect.height + padding * 2}px`,
          backgroundColor: 'rgba(0, 0, 0, 0.2)',
          pointerEvents: 'none',
          zIndex: 9997,
          transition: 'all 0.3s ease-in-out',
        },
        highlight: {
          position: 'fixed',
          top: `${rect.top - padding}px`,
          left: `${rect.left - padding}px`,
          width: `${rect.width + padding * 2}px`,
          height: `${rect.height + padding * 2}px`,
          pointerEvents: 'none',
          zIndex: 9998,
          transition: 'all 0.3s ease-in-out',
        },
      });
      setIsVisible(true);
    } else {
      setIsVisible(false);
    }
  };

  useEffect(() => {
    // Restore previous element's styles before highlighting new one
    if (targetRef.current && prevStylesRef.current) {
      targetRef.current.style.position = prevStylesRef.current.position || '';
      targetRef.current.style.zIndex = prevStylesRef.current.zIndex || '';
      prevStylesRef.current = null;
    }

    const timer = setTimeout(updatePositions, 50);

    // Recalculate on resize/scroll to keep the cutout aligned
    window.addEventListener('resize', updatePositions);
    window.addEventListener('scroll', updatePositions, true);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updatePositions);
      window.removeEventListener('scroll', updatePositions, true);

      // Restore original styles on cleanup
      if (targetRef.current && prevStylesRef.current) {
        targetRef.current.style.position = prevStylesRef.current.position || '';
        targetRef.current.style.zIndex = prevStylesRef.current.zIndex || '';
      }
      prevStylesRef.current = null;
      targetRef.current = null;
    };
  }, [targetNavHref]);

  if (!isVisible || !overlayStyles) return null;

  return (
    <>
      {/* Four overlay sections that create the dimming effect with a cutout */}
      <div style={overlayStyles.top} />
      <div style={overlayStyles.right} />
      <div style={overlayStyles.bottom} />
      <div style={overlayStyles.left} />

      {/* Spotlight ring around nav item (no darkening inside the cutout) */}
      <div
        style={overlayStyles.highlight}
        className="rounded-lg ring-4 ring-primary ring-offset-2 ring-offset-background"
      />
    </>
  );
}

